import { normalizeRouterQuery } from '@automata/utils';
import { NextRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import { AppStore, createStore } from '../../store';

/**
 * Hook that handles getting a redux store for a given workflow.
 * When we change workflow we create a new empty redux store so
 * we don't unintentionally carry any state over between workflows.
 *
 * We do this by creating a new redux store when the workflow id changes.
 * As a result there's a bit of a song and dance to do to handle the case where
 * we move from a new, unsaved workflow (which doesn't have an id yet) to a saved workflow.
 * In this case, the "workflowId" query param will change from "new" to the id of the saved workflow,
 * but we don't want to lose the undo history from the unsaved workflow so we don't want to
 * recreate the redux store.
 */
export const useWorkflowStore = (router: NextRouter): AppStore => {
  const normalizedQuery = normalizeRouterQuery(router.query);

  const workflowId = normalizedQuery.id ?? '';
  const initialSave = normalizedQuery.initialSave;

  const workflowStoreRef = useRef<{
    store: AppStore;
    workflowId: string;
  }>();

  if (!workflowStoreRef.current) {
    // on first render, create an empty redux store
    workflowStoreRef.current = { workflowId, store: createStore() };
  } else if (
    initialSave &&
    workflowStoreRef.current.workflowId === 'new' &&
    workflowId !== 'new'
  ) {
    // we indicate the first save of a workflow using an 'initialSave' query param.
    // if this is set, we update the workflowId in the ref to the id of the newly created workflow
    // we've just redirected to, but reuse the store so we retain undo history
    workflowStoreRef.current.workflowId = workflowId;
  } else if (workflowStoreRef.current.workflowId !== workflowId) {
    // otherwise, if the workflowId has changed, we create a new empty redux store
    workflowStoreRef.current = { workflowId, store: createStore() };
  }

  // remove the 'initialSave' query params from the url
  // if they are set, to avoid any potential confusion later
  useEffect(() => {
    if (initialSave) {
      const { initialSave: _initialSave, ...query } = router.query;
      router.replace({ query });
    }
  }, [router, initialSave]);

  return workflowStoreRef.current.store;
};
